<template>
  <div class="w-full">
    <div class="flex p-20">
      <div class="flex flex-col w-1/3">
        <div class="text-16 font-bold">Suppliers</div>
        <div class="text-13 text-primary-500 cursor-pointer hover:text-primary-600" @click="openAddModal">Add</div>
      </div>
      <div class="flex flex-col w-2/3 border border-gray-50 rounded-8">
        <table class="w-full border-collapse">
          <thead>
            <tr class="border border-gray-50 text-14 font-medium rounded-8">
              <th class="p-16 text-left w-1/3">Supplier</th>
              <th class="p-16 text-left w-1/3">Contact</th>
              <th class="p-16 text-left w-1/6">Combinations</th>
              <th class="p-16 text-left w-1/6">Qualified</th>
              <th class="p-16 w-8" />
            </tr>
          </thead>
          <tbody>
            <tr v-for="supplier in combinedSuppliers" :key="supplier.uid" class="border-b border-gray-50 relative">
              <td class="p-16">
                <div class="flex items-center">
                  <div class="w-40 h-40 mr-16 flex items-center justify-center">
                    <img :src="supplier.logoUrl || defaultLogo" alt="Supplier logo" class="max-w-full max-h-full" />
                  </div>
                  <div>
                    <div class="text-14 font-medium text-gray-1000 flex items-center">
                      {{ supplier.name }}
                      <SparkBadge v-if="supplier.qualified" class="ml-8" variant="success"
                        ><div>Qualified</div></SparkBadge
                      >
                    </div>
                    <div class="text-13 text-gray-400">
                      {{ supplier.restriction === 'public' ? 'Public' : 'Self Created' }}
                    </div>
                  </div>
                </div>
              </td>
              <td class="p-16">
                <div class="text-14 font-medium text-gray-1000">
                  {{ supplier.contact_email }}
                </div>
              </td>
              <td class="p-16">
                <span class="text-13 text-gray-1000">{{ supplier.supplier_combination_count }}</span>
              </td>
              <td class="p-16">
                <div
                  v-if="supplier.is_qualified"
                  class="w-24 h-24 rounded-full text-center bg-primary-100 flex items-center justify-center"
                >
                  <SparkButtonIcon icon="fas fa-check" variant="text" size="sm" custom="text-primary-600" />
                </div>
              </td>
              <td class="p-16 text-right">
                <DropdownMenu
                  v-show="dropdownOptions[supplier.uid]"
                  :list-options="dropdownOptions[supplier.uid]"
                  title-icon="fas fa-ellipsis-h"
                  close-click-style="always"
                  @update-option="handleAction($event, supplier)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <CreateSupplierModal :supplier-mode="mode" :supplier="supplierToEdit" :show="showModal" @close="closeModal" />
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex';

import CreateSupplierModal from './CreateSupplierModal.vue';

import defaultImage from '@/assets/img/Logo_Transparent.png';
import DropdownMenu from '@/components/Reusable/DropdownMenu.vue';
import SparkBadge from '@/components/SparkComponents/SparkBadge.vue';
import SparkButtonIcon from '@/components/SparkComponents/SparkButtonIcon.vue';

export default {
  name: 'SupplierTable',

  components: {
    SparkButtonIcon,
    SparkBadge,
    DropdownMenu,
    CreateSupplierModal,
  },

  emits: ['update'],

  data() {
    return {
      showModal: false,
      //   dropdownOptions: [
      //     { text: 'Edit', value: 'edit', icon: 'fas fa-edit' },
      //     { text: 'Set as qualified', value: 'set-as-qualified', icon: 'fas fa-check' },
      //     { text: 'Delete', value: 'delete', icon: 'fas fa-trash' },
      //   ],
      dropdownOptions: {},
      supplierToEdit: null,
      mode: 'create',
    };
  },

  computed: {
    ...mapState('supplier', ['publicSuppliers', 'selectedSuppliers', 'customersSuppliers']),

    combinedSuppliers() {
      return [...this.customersSuppliers, ...this.publicSuppliers];
    },

    selectedSuppliersValue: {
      get() {
        return this.selectedSuppliers;
      },

      set(value) {
        this.setSelectedSuppliers(value);
      },
    },

    defaultLogo() {
      return defaultImage;
    },
  },

  watch: {
    combinedSuppliers: {
      handler() {
        this.calculateDropownOptions();
      },
    },
  },

  methods: {
    ...mapActions('supplier', ['updateSuppliers', 'deleteSupplier', 'updateSupplierAction']),
    ...mapMutations('supplier', ['setSelectedSuppliers']),

    openAddModal() {
      this.supplierToEdit = null;
      this.mode = 'create';
      this.showModal = true;
    },

    handleAction(option, supplier) {
      switch (option.value) {
        case 'edit':
          this.supplierToEdit = supplier;
          this.mode = 'edit';
          this.showModal = true;
          break;
        case 'delete':
          this.deleteSupplier(supplier);
          break;
        case 'remove':
          this.removeFromInMyProduction(supplier);
          break;
        case 'add':
          this.addToInMyProduction(supplier);
          break;
        case 'set-as-qualified':
          this.setAsQualified(supplier);
          break;
        case 'remove-as-qualified':
          this.removeAsQualified(supplier);
          break;
      }
    },

    closeModal() {
      this.showModal = false;
    },

    calculateDropownOptions() {
      this.combinedSuppliers.forEach(supplier => {
        const isQualified = supplier.is_qualified;
        const isInMyProduction = this.checkIfSupplierInMyProduction(supplier.uid);
        const qualifiedText = isQualified ? 'Remove as qualified' : 'Set as qualified';
        const qualifiedValue = isQualified ? 'remove-as-qualified' : 'set-as-qualified';

        if (supplier.restriction === 'customers' && isInMyProduction) {
          this.dropdownOptions[supplier.uid] = [
            { text: 'Delete', value: 'remove', icon: 'fas fa-trash', disabled: false },
            { text: 'Edit', value: 'edit', icon: 'fas fa-edit', disabled: false },
            { text: qualifiedText, value: qualifiedValue, icon: 'fas fa-check', disabled: false },
          ];
        }
      });
    },

    checkIfSupplierInMyProduction(uid) {
      let isInMyProduction = false;
      Object.keys(this.selectedSuppliersValue).forEach(key => {
        if (uid === this.selectedSuppliers[key].uid) {
          isInMyProduction = true;
        }
      });
      return isInMyProduction;
    },

    async removeFromInMyProduction(supplier) {
      await this.deleteSupplier({
        uid: supplier.uid,
        notify: this.$root.notify,
      });
      this.calculateDropownOptions();
    },

    addToInMyProduction(supplier) {
      this.selectedSuppliersValue.push(supplier);
      this.setSelectedSuppliers(this.selectedSuppliersValue);
      this.updateSupplierList('add');
      this.calculateDropownOptions();
    },

    updateSupplierList(operation = 'update') {
      this.updateSuppliers({
        notify: this.$root.notify,
        operation: operation,
      });

      this.$emit('update');
    },

    async setAsQualified(supplier) {
      try {
        await this.updateSupplierAction({
          uid: supplier.uid,
          data: {
            is_qualified: true,
          },
        });
        await Promise.all([
          this.$store.dispatch('supplier/fetchSelectedSuppliers'),
          this.$store.dispatch('supplier/fetchCustomersSuppliers'),
        ]);
        this.calculateDropownOptions();
        this.updateSupplierList('qualify');
      } catch (error) {
        console.error('Error toggling supplier qualification:', error);
      }
    },

    async removeAsQualified(supplier) {
      try {
        await this.updateSupplierAction({
          uid: supplier.uid,
          data: {
            is_qualified: false,
          },
        });
        this.calculateDropownOptions();
        this.updateSupplierList('unqualify');
      } catch (error) {
        console.error('Error removing supplier qualification:', error);
      }
    },
  },
};
</script>
